"use client";
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Modal,
  Table,
  Badge,
} from "react-bootstrap";
import { FaPlus, FaEye, FaRegEdit, FaTrash } from "react-icons/fa";
import {
  collection,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { uploadToCloudinary, uploadVideoToCloudinary, isVideoFile, isVideoUrl } from "../cloudinaryConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

const schema = Yup.object({
  name: Yup.string().required("Product name is required"),
  category: Yup.string().required("Category is required"),
  price: Yup.number()
    .transform((value, originalValue) => originalValue === "" ? undefined : value)
    .required("Price is required")
    .positive("Price must be positive"),
  sku: Yup.string(),
  description: Yup.string(),
  size: Yup.string(),
  material: Yup.string(),
});

const emptyValues = {
  name: "",
  sku: "",
  category: "",
  price: "",
  offer: 0,
  description: "",
  size: "",
  material: "",
  stock: true,
};

export default function AddProductsModernUI() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [existingMediaUrls, setExistingMediaUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMainIdx, setViewMainIdx] = useState(0);
  const [loadingView, setLoadingView] = useState(false);

  useEffect(() => {
    const u1 = onSnapshot(collection(db, "products"), (s) =>
      setRows(s.docs.map((d) => {
        const data = d.data();
        const images = data.image ?? data.images;
        const firstImage = Array.isArray(images) ? images[0] : images;
        return {
          id: d.id,
          ...data,
          image: firstImage ? [firstImage] : [],
        };
      }))
    );
    const u2 = onSnapshot(collection(db, "categories"), (s) =>
      setCategories(s.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => {
      u1();
      u2();
    };
  }, []);

  const formik = useFormik({
    initialValues: emptyValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const totalMedia = mediaFiles.length + existingMediaUrls.length;
      if (!editing && totalMedia === 0) {
        toast.error("Please add at least one product image or video!");
        return;
      }

      setUploading(true);
      try {
        const uploadedUrls = [];
        if (mediaFiles.length > 0) {
          for (let i = 0; i < mediaFiles.length; i++) {
            const file = mediaFiles[i];
            const isVideo = isVideoFile(file);

            if (isVideo) {
              setUploadProgress(`Uploading video ${i + 1}/${mediaFiles.length}...`);
              const url = await uploadVideoToCloudinary(file, (percent) => {
                setUploadProgress(`Uploading video ${i + 1}/${mediaFiles.length} (${percent}%)...`);
              });
              uploadedUrls.push(url);
            } else {
              setUploadProgress(`Uploading image ${i + 1}/${mediaFiles.length}...`);
              const url = await uploadToCloudinary(file);
              uploadedUrls.push(url);
            }
          }
        }

        const allMediaUrls = [...existingMediaUrls, ...uploadedUrls];

        setUploadProgress("Saving product...");

        const payload = {
          ...values,
          price: Number(values.price),
          offer: Number(values.offer || 0),
          image: allMediaUrls,
          updatedAt: new Date(),
        };

        if (editing) {
          await updateDoc(doc(db, "products", editing.id), payload);
          toast.success("Product Updated Successfully!");
        } else {
          await addDoc(collection(db, "products"), {
            ...payload,
            createdAt: new Date(),
          });
          toast.success("Product Added Successfully!");
        }
        setOpen(false);
        setEditing(null);
        setMediaFiles([]);
        setMediaPreviews([]);
        setMediaTypes([]);
        setExistingMediaUrls([]);
        setUploadProgress("");
        formik.resetForm();

      } catch (error) {
        console.error("Save error:", error);
        toast.error(`Save failed: ${error.message || 'Please try again'}`);
      } finally {
        setUploading(false);
        setUploadProgress("");
      }
    },
  });

  const pickMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = [];
    const previews = [];
    const types = [];

    for (const f of files) {
      const isVideo = isVideoFile(f);
      const isImage = f.type.startsWith("image/");

      if (!isVideo && !isImage) {
        toast.error(`${f.name} is not a valid image or video file.`);
        continue;
      }

      if (isImage && (f.type === 'image/heic' || f.type === 'image/heif')) {
        toast.error(`${f.name}: HEIC/HEIF format not supported. Please convert to JPG/PNG.`);
        continue;
      }

      if (isVideo && f.size > 50 * 1024 * 1024) {
        toast.error(`${f.name}: Video must be under 50MB.`);
        continue;
      }

      try {
        let previewUrl;
        if (isVideo) {
          previewUrl = URL.createObjectURL(f);
        } else {
          previewUrl = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onloadend = () => res(r.result);
            r.onerror = () => rej(new Error('Failed to read file'));
            r.readAsDataURL(f);
          });
        }

        validFiles.push(f);
        previews.push(previewUrl);
        types.push(isVideo ? 'video' : 'image');
      } catch (error) {
        console.error(`Error loading ${f.name}:`, error);
        toast.error(`Failed to load ${f.name}`);
      }
    }

    const currentTotal = existingMediaUrls.length + mediaFiles.length;
    const maxAllowed = 6;

    if (currentTotal + validFiles.length > maxAllowed) {
      const canAdd = maxAllowed - currentTotal;
      if (canAdd <= 0) {
        toast.warning(`Maximum ${maxAllowed} files allowed per product!`);
        return;
      }
      toast.warning(`Only adding ${canAdd} file(s). Maximum ${maxAllowed} allowed.`);
      setMediaFiles((prev) => [...prev, ...validFiles.slice(0, canAdd)]);
      setMediaPreviews((prev) => [...prev, ...previews.slice(0, canAdd)]);
      setMediaTypes((prev) => [...prev, ...types.slice(0, canAdd)]);
    } else if (validFiles.length > 0) {
      setMediaFiles((prev) => [...prev, ...validFiles]);
      setMediaPreviews((prev) => [...prev, ...previews]);
      setMediaTypes((prev) => [...prev, ...types]);
      toast.success(`${validFiles.length} file(s) selected!`);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setMediaFiles([]);
    setMediaPreviews([]);
    setMediaTypes([]);
    setExistingMediaUrls([]);
    formik.resetForm();
    setOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const docRef = doc(db, "products", row.id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const fullProduct = { id: docSnap.id, ...docSnap.data() };
        setEditing(fullProduct);
        setExistingMediaUrls(fullProduct.image || []);
        setMediaFiles([]);
        setMediaPreviews([]);
        setMediaTypes([]);
        formik.setValues({ ...emptyValues, ...fullProduct });
        setOpen(true);
      } else {
        toast.error("Product not found");
      }
    } catch (error) {
      console.error("Error fetching product for edit:", error);
      toast.error("Failed to load product");
    }
  };

  const removeExistingMedia = (index) => {
    setExistingMediaUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewMedia = (index) => {
    if (mediaTypes[index] === 'video') {
      URL.revokeObjectURL(mediaPreviews[index]);
    }
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
    setMediaPreviews((prev) => prev.filter((_, i) => i !== index));
    setMediaTypes((prev) => prev.filter((_, i) => i !== index));
  };

  const removeRow = (id) => {
    confirmAlert({
      title: "Confirm to Delete",
      message: "Are you sure you want to delete this product?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              await deleteDoc(doc(db, "products", id));
              toast.success("Product deleted successfully!");
            } catch (error) {
              console.error("Error deleting product:", error);
              toast.error("Failed to delete product.");
            }
          },
        },
        {
          label: "No",
          onClick: () => { },
        },
      ],
    });
  };

  const toggleStockStatus = async (productId, currentStock) => {
    try {
      const docRef = doc(db, "products", productId);
      await updateDoc(docRef, { stock: !currentStock });
      toast.success(`Stock marked as ${!currentStock ? 'In Stock' : 'Out of Stock'}`);
    } catch (error) {
      console.error("Error updating stock:", error);
      toast.error("Failed to update stock status.");
    }
  };

  const viewProduct = async (productId) => {
    setLoadingView(true);
    try {
      const docRef = doc(db, "products", productId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setSelectedProduct({ id: docSnap.id, ...docSnap.data() });
        setViewMainIdx(0);
        setShowDetailModal(true);
      } else {
        toast.error("Product not found");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product details");
    } finally {
      setLoadingView(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <ToastContainer />
      <Card className="admin-card border-0 mb-4">
        <div className="admin-card-header">
          <h4 className="admin-card-title">Products Management</h4>
          <Button style={{ backgroundColor: "var(--admin-primary)", borderColor: "var(--admin-primary)" }} onClick={openAdd} className="d-flex align-items-center gap-2 shadow-sm">
            <FaPlus /> Add Product
          </Button>
        </div>
      </Card>

      <Card className="admin-card border-0">
        <Card.Body className="p-0">
          <div className="admin-table-container">
            <Table responsive className="admin-table mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="px-4 py-3">Image</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Price / Offer</th>
                  <th className="px-4 py-3">Stock</th>
                  <th className="px-4 py-3 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-4">
                        {row.image && row.image.length > 0 ? (
                          <img src={row.image[0]} alt="thumb" style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "6px" }} />
                        ) : (
                          <div style={{ width: "56px", height: "56px", backgroundColor: "#f0f0f0", borderRadius: "6px" }}></div>
                        )}
                      </td>
                      <td className="px-4 fw-medium">{row.name}</td>
                      <td className="px-4">{row.category}</td>
                      <td className="px-4">
                        {row.offer > 0 ? (
                          <div>
                            <span className="text-muted text-decoration-line-through d-block" style={{ fontSize: "14px" }}>₹{row.price}</span>
                            <span className="fw-bold d-block">₹{Math.round(row.price * (1 - row.offer / 100) * 100) / 100}</span>
                            <span className="text-primary fw-bold" style={{ fontSize: "13px" }}>{row.offer}% OFF</span>
                          </div>
                        ) : (
                          <span>₹{row.price}</span>
                        )}
                      </td>
                      <td className="px-4">
                        <span
                          className={`admin-badge bg-${row.stock ? "success" : "secondary"} text-white`}
                          style={{ cursor: "pointer", transition: "opacity 0.2s" }}
                          onClick={() => toggleStockStatus(row.id, row.stock)}
                          title="Click to change stock status"
                        >
                          {row.stock ? "In Stock" : "Out"}
                        </span>
                      </td>
                      <td className="px-4 text-end">
                        <div className="admin-action-btns">
                          <button type="button" className="btn-action btn-action-view" onClick={() => viewProduct(row.id)} disabled={loadingView} title="View">
                            <FaEye size={18} />
                          </button>
                          <button type="button" className="btn-action btn-action-edit" onClick={() => openEdit(row)} title="Edit">
                            <FaRegEdit size={18} />
                          </button>
                          <button type="button" className="btn-action btn-action-delete" onClick={() => removeRow(row.id)} title="Delete">
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">No products found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={open} onHide={() => setOpen(false)} size="lg" centered backdrop="static" fullscreen="md-down">
        <Modal.Header closeButton className="border-bottom px-3 px-md-4 py-3 bg-light">
          <Modal.Title className="fw-bold m-0" style={{ fontSize: "1.25rem", color: "var(--admin-text-main)" }}>
            {editing ? "Edit Product" : "Add Product"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 p-md-4">
          <Form onSubmit={formik.handleSubmit}>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Product Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formik.values.name}
                    onChange={(e) => {
                      const val = e.target.value;
                      formik.setFieldValue("name", val);

                      // Auto-generate SKU when adding a new product
                      if (!editing) {
                        const generatedSku = val.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '');
                        formik.setFieldValue("sku", generatedSku);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={!!(formik.touched.name && formik.errors.name)}
                  />
                  <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">SKU</Form.Label>
                  <Form.Control type="text" {...formik.getFieldProps("sku")} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Category *</Form.Label>
                  <Form.Select
                    {...formik.getFieldProps("category")}
                    isInvalid={!!(formik.touched.category && formik.errors.category)}
                  >
                    <option value="">Select Category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{formik.errors.category}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="fw-medium">Price *</Form.Label>
                  <Form.Control
                    type="number"
                    {...formik.getFieldProps("price")}
                    isInvalid={!!(formik.touched.price && formik.errors.price)}
                  />
                  <Form.Control.Feedback type="invalid">{formik.errors.price}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="fw-medium">Offer %</Form.Label>
                  <Form.Control type="number" {...formik.getFieldProps("offer")} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-medium">Description</Form.Label>
                  <Form.Control as="textarea" rows={4} {...formik.getFieldProps("description")} placeholder="Product description..." />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium">Size</Form.Label>
                  <Form.Control type="text" {...formik.getFieldProps("size")} />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="form-label">Material</Form.Label>
                  <Form.Control type="text" {...formik.getFieldProps("material")} />
                </Form.Group>
              </Col>
              <Col xs={12} md={4} className="d-flex flex-column pt-md-2">
                <Form.Label className="fw-medium mb-2">Stock Availability</Form.Label>
                <Form.Check
                  type="switch"
                  id="stock-switch"
                  label={formik.values.stock ? "Yes, In Stock" : "No, Out of Stock"}
                  checked={formik.values.stock}
                  onChange={(e) => formik.setFieldValue("stock", e.target.checked)}
                />
              </Col>

              <Col xs={12}>
                <hr className="my-2" />
                <Button variant="outline-secondary" className="position-relative w-100">
                  Upload Images / Videos
                  <Form.Control
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={pickMedia}
                    className="position-absolute top-0 start-0 w-100 h-100 opacity-0"
                    style={{ cursor: "pointer" }}
                  />
                </Button>
                <div className="text-muted small mt-1">Images: JPG, PNG | Videos: MP4, WebM (max 50MB)</div>
              </Col>

              {existingMediaUrls.length > 0 && (
                <Col xs={12}>
                  <div className="fw-medium small text-muted mb-2">Existing Media</div>
                  <div className="p-2 border border-success rounded border-dashed">
                    <Row className="g-2">
                      {existingMediaUrls.map((url, idx) => (
                        <Col xs={6} md={3} key={`existing-${idx}`}>
                          <div className="position-relative border rounded p-1 bg-light">
                            {isVideoUrl(url) ? (
                              <video src={url} style={{ width: "100%", height: "100px", objectFit: "contain", background: "#000" }} controls muted />
                            ) : (
                              <img src={url} alt={`existing-${idx}`} style={{ width: "100%", height: "100px", objectFit: "contain" }} />
                            )}
                            {isVideoUrl(url) && <Badge bg="dark" className="position-absolute bottom-0 start-0 m-1" style={{ fontSize: "10px" }}>Video</Badge>}
                            <Button variant="danger" size="sm" className="position-absolute top-0 end-0 m-1 p-1 lh-1" onClick={() => removeExistingMedia(idx)}>
                              <FaTrash size={12} />
                            </Button>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Col>
              )}

              {mediaPreviews.length > 0 && (
                <Col xs={12}>
                  <div className="fw-medium small text-muted mb-2">New Media (will be uploaded)</div>
                  <div className="p-2 border border-primary rounded border-dashed">
                    <Row className="g-2">
                      {mediaPreviews.map((preview, idx) => (
                        <Col xs={6} md={3} key={`new-${idx}`}>
                          <div className="position-relative border rounded p-1 bg-light">
                            {mediaTypes[idx] === 'video' ? (
                              <video src={preview} style={{ width: "100%", height: "100px", objectFit: "contain", background: "#000" }} controls muted />
                            ) : (
                              <img src={preview} alt={`preview-${idx}`} style={{ width: "100%", height: "100px", objectFit: "contain" }} />
                            )}
                            {mediaTypes[idx] === 'video' && <Badge bg="dark" className="position-absolute bottom-0 start-0 m-1" style={{ fontSize: "10px" }}>Video</Badge>}
                            <Button variant="danger" size="sm" className="position-absolute top-0 end-0 m-1 p-1 lh-1" onClick={() => removeNewMedia(idx)}>
                              <FaTrash size={12} />
                            </Button>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Col>
              )}

              <Col xs={12}>
                <Button
                  type="submit"
                  size="lg"
                  className="w-100 fw-bold mt-2 shadow-sm"
                  disabled={uploading}
                  style={{ backgroundColor: "var(--admin-primary)", borderColor: "var(--admin-primary)" }}
                >
                  {uploading ? (uploadProgress || "Processing...") : (editing ? "Update Product" : "Create Product")}
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Product Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-primary">Product Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 p-md-4">
          {selectedProduct && (
            <Row className="g-4">
              <Col xs={12} md={6}>
                <div className="bg-light rounded p-2 mb-3" style={{ height: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {selectedProduct.image && selectedProduct.image.length > 0 ? (
                    isVideoUrl(selectedProduct.image[viewMainIdx]) ? (
                      <video src={selectedProduct.image[viewMainIdx]} controls autoPlay style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    ) : (
                      <img src={selectedProduct.image[viewMainIdx]} alt={selectedProduct.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    )
                  ) : (
                    <div className="text-muted">No Image</div>
                  )}
                </div>

                {selectedProduct.image && selectedProduct.image.length > 0 && (
                  <div className="d-flex overflow-auto gap-2 pb-2">
                    {selectedProduct.image.map((media, i) => (
                      <div
                        key={i}
                        onClick={() => setViewMainIdx(i)}
                        className={`border rounded flex-shrink-0 position-relative ${viewMainIdx === i ? 'border-primary' : 'border-light'}`}
                        style={{ width: "60px", height: "60px", cursor: "pointer", opacity: viewMainIdx === i ? 1 : 0.7 }}
                      >
                        {isVideoUrl(media) ? (
                          <>
                            <video src={media} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <div className="position-absolute top-50 start-50 translate-middle text-white bg-dark rounded-circle d-flex align-items-center justify-content-center" style={{ width: "20px", height: "20px", fontSize: "10px", opacity: 0.8 }}>▶</div>
                          </>
                        ) : (
                          <img src={media} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Col>

              <Col xs={12} md={6}>
                <div className="text-muted text-uppercase small fw-bold mb-1">{selectedProduct.category}</div>
                <h4 className="fw-bold mb-3">{selectedProduct.name}</h4>

                <div className="d-flex align-items-baseline gap-2 mb-4">
                  <h3 className="fw-bold text-primary mb-0">
                    ₹{selectedProduct.price ? (selectedProduct.price * (1 - (selectedProduct.offer || 0) / 100)).toFixed(0) : '0'}
                  </h3>
                  {selectedProduct.offer > 0 && (
                    <>
                      <span className="text-muted text-decoration-line-through">₹{selectedProduct.price}</span>
                      <Badge bg="primary">{selectedProduct.offer}% OFF</Badge>
                    </>
                  )}
                </div>

                <Row className="g-3 mb-4">
                  <Col xs={6}>
                    <div className="text-muted small fw-medium">SKU</div>
                    <div className="fw-medium">{selectedProduct.sku || '-'}</div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-muted small fw-medium">Stock Status</div>
                    <Badge bg={selectedProduct.stock ? "success" : "secondary"}>
                      {selectedProduct.stock ? "In Stock" : "Out of Stock"}
                    </Badge>
                  </Col>
                  <Col xs={6}>
                    <div className="text-muted small fw-medium">Dimensions</div>
                    <div>{selectedProduct.size || '-'}</div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-muted small fw-medium">Material</div>
                    <div>{selectedProduct.material || '-'}</div>
                  </Col>
                </Row>

                <div>
                  <div className="fw-bold mb-1">Description</div>
                  <p className="text-muted small">{selectedProduct.shortDescription || 'No description available.'}</p>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
